import { createStore } from '@shared/utils/state/state-factory'
import { TURNS_LIMITS } from '../../tabs/eval-constants'
import type { RResponseItem, RToolCall } from '../../tabs/simulation-tab.queries'

export interface SimulationEvent {
  id: string
  eventName: string
  data: unknown
  receivedAt: Date
}

export interface SimulationScenario {
  id: string
  name: string
  persona: {
    personality: string
    emotional_state?: string
    behavioral_traits?: string[]
    details?: Record<string, unknown>
  }
  goal: string
  tags: string[]
  maxTurns: number
  worldState?: Record<string, unknown>
  startNodeParams?: Record<string, unknown>
}

export interface SimulationTurn {
  turn: number
  user: string
  agent: string
  intent: string
  nodeUuid: string
  variables: Record<string, unknown>
  variablesByNode: Record<string, Record<string, unknown>>
  toolCalls: RToolCall[]
  responseItems: RResponseItem[]
  isInterruption: boolean
  partialAssistantMsg: string
}

export interface ScenarioResult {
  stopReason: string
  turns: number
  evaluation?: Record<string, unknown>
  evalError?: boolean
  error?: string
}

export type ScenarioStatus = 'pending' | 'running' | 'completed' | 'error' | 'cancelled'
export type SimulationStatus =
  | 'idle'
  | 'enqueued'
  | 'started'
  | 'running'
  | 'completed'
  | 'error'
  | 'cancelled'

export type GenerationStatus =
  | 'idle'
  | 'planning'
  | 'allocating'
  | 'writing'
  | 'completed'
  | 'error'

export interface GenerationProgress {
  stage?: string
  requested_count?: number
  planned_count?: number
  saved_count?: number
  failed_count?: number
  chunk_count?: number
  chunk_index?: number
  failed_slot_ids?: string[]
  partial_success?: boolean
  error_type?: string
  error?: string
}

export interface ScenarioState {
  scenario: SimulationScenario
  status: ScenarioStatus
  turns: SimulationTurn[]
  result?: ScenarioResult
  flowRunUuid?: string
  dbUuid?: string
}

interface SimulationState {
  events: SimulationEvent[]
  simulationStatus: SimulationStatus
  scenarioStates: Record<string, ScenarioState>
  scenarioOrder: string[]
  selectedScenarioId: string | null
  agentFlowDescription: string | null
  errorMessage: string | null
  activeRunUuid: string | null
  sseController: AbortController | null
  generationStatus: GenerationStatus
  generationProgress: GenerationProgress | null
  activeGenerationId: string | null
  generationError: string | null
}

interface SimulationActions {
  addEvent: (eventName: string, data: unknown) => void
  clear: () => void
  selectScenario: (id: string | null) => void
  cancelSimulation: () => void
  setSseController: (ctrl: AbortController | null) => void
}

const INITIAL_STRUCTURED_STATE = {
  simulationStatus: 'idle' as SimulationStatus,
  scenarioStates: {} as Record<string, ScenarioState>,
  scenarioOrder: [] as string[],
  selectedScenarioId: null as string | null,
  agentFlowDescription: null as string | null,
  errorMessage: null as string | null,
  activeRunUuid: null as string | null,
  sseController: null as AbortController | null,
  generationStatus: 'idle' as GenerationStatus,
  generationProgress: null as GenerationProgress | null,
  activeGenerationId: null as string | null,
  generationError: null as string | null,
}

const GENERATION_STAGE_TO_STATUS: Record<string, GenerationStatus> = {
  planning_started: 'planning',
  planning_done: 'planning',
  allocation_started: 'allocating',
  allocation_done: 'writing',
  writing_started: 'writing',
  writer_chunk_retrying: 'writing',
  writer_chunk_failed: 'writing',
  writer_chunk_done: 'writing',
}

const isGenerationActive = (status: GenerationStatus): boolean =>
  status !== 'idle' && status !== 'completed' && status !== 'error'

export const selectIsGenerating = (s: SimulationState): boolean =>
  isGenerationActive(s.generationStatus)

const mergeScenarioPayload = (
  existing: SimulationScenario | undefined,
  payload: Record<string, unknown>,
): SimulationScenario => {
  const persona = (payload.persona ??
    existing?.persona ?? {
      personality: '',
    }) as SimulationScenario['persona']

  return {
    id: (payload.id as string) ?? existing?.id ?? '',
    name: (payload.name as string) ?? existing?.name ?? '',
    persona,
    goal: (payload.goal as string) ?? existing?.goal ?? '',
    tags: (payload.tags as string[]) ?? existing?.tags ?? [],
    maxTurns: (payload.max_turns as number) ?? existing?.maxTurns ?? TURNS_LIMITS.DEFAULT,
    worldState:
      (payload.world_state as Record<string, unknown> | undefined) ?? existing?.worldState,
    startNodeParams:
      (payload.start_node_params as Record<string, unknown> | undefined) ??
      existing?.startNodeParams,
  }
}

const upsertScenarioPayload = (
  state: Pick<SimulationState, 'scenarioStates' | 'scenarioOrder'>,
  payload: Record<string, unknown>,
) => {
  const id = payload.id as string
  if (!id) return

  const existing = state.scenarioStates[id]
  if (existing) {
    existing.scenario = mergeScenarioPayload(existing.scenario, payload)
    return
  }

  state.scenarioStates[id] = {
    scenario: mergeScenarioPayload(undefined, payload),
    status: 'pending',
    turns: [],
  }
  state.scenarioOrder.push(id)
}

export const useSimulationStore = createStore<SimulationState & SimulationActions>(
  'simulation',
  (set) => ({
    events: [],
    ...INITIAL_STRUCTURED_STATE,

    addEvent: (eventName, data) =>
      set((state) => {
        state.events.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          eventName,
          data,
          receivedAt: new Date(),
        })

        const envelope = data as Record<string, unknown> | null
        const eventData = (envelope?.event_data ?? data) as Record<string, unknown>

        switch (eventName) {
          case 'started':
            state.simulationStatus = 'enqueued'
            state.activeRunUuid = (eventData.simulation_run_uuid as string) ?? null
            break

          case 'simulation_started':
            state.simulationStatus = 'started'
            break

          case 'scenarios_generated': {
            state.simulationStatus = 'running'
            const scenarios = eventData.scenarios as Array<Record<string, unknown>>
            if (scenarios) {
              for (const s of scenarios) {
                upsertScenarioPayload(state, s)
              }
            }
            state.agentFlowDescription = (eventData.agent_flow_description as string) ?? null
            break
          }

          case 'scenario_generated':
          case 'scenario_saved': {
            state.simulationStatus = 'running'
            const s = eventData.scenario as Record<string, unknown>
            if (s) {
              upsertScenarioPayload(state, s)
            }
            if (eventData.agent_flow_description) {
              state.agentFlowDescription = eventData.agent_flow_description as string
            }
            if (isGenerationActive(state.generationStatus)) {
              state.generationStatus = 'writing'
              const next = (state.generationProgress ?? {}) as GenerationProgress
              const saved = (next.saved_count ?? 0) + 1
              state.generationProgress = {
                ...next,
                stage: 'writer_chunk_done',
                saved_count: saved,
              }
            }
            break
          }

          case 'progress': {
            const stage = eventData.stage as string | undefined
            const mappedStatus: GenerationStatus | undefined = stage
              ? GENERATION_STAGE_TO_STATUS[stage]
              : undefined
            state.generationStatus = mappedStatus ?? state.generationStatus
            const incoming = eventData as Partial<GenerationProgress> & {
              generation_id?: string
            }
            const current = state.generationProgress ?? {}
            state.generationProgress = {
              ...current,
              ...incoming,
              saved_count: Math.max(current.saved_count ?? 0, incoming.saved_count ?? 0),
            }
            if (!state.activeGenerationId && incoming.generation_id) {
              state.activeGenerationId = incoming.generation_id
            }
            break
          }

          case 'completed': {
            if (isGenerationActive(state.generationStatus)) {
              state.generationStatus = 'completed'
              const incoming = eventData as Partial<GenerationProgress>
              if (incoming.partial_success) {
                const current = state.generationProgress ?? {}
                state.generationProgress = {
                  ...current,
                  ...incoming,
                  saved_count: Math.max(current.saved_count ?? 0, incoming.saved_count ?? 0),
                }
              } else {
                state.generationProgress = null
              }
            }
            break
          }

          case 'error': {
            if (isGenerationActive(state.generationStatus)) {
              state.generationStatus = 'error'
              state.generationError = (eventData.error as string) ?? 'Scenario generation failed'
              state.generationProgress = null
            } else {
              state.simulationStatus = 'error'
              state.errorMessage =
                (eventData.error as string) ?? (eventData.message as string) ?? 'Unknown error'
            }
            break
          }

          case 'scenario_db_ready': {
            const scenarioId = eventData.scenario_id as string
            if (state.scenarioStates[scenarioId]) {
              state.scenarioStates[scenarioId].dbUuid = eventData.scenario_db_uuid as string
            }
            break
          }

          case 'scenario_started': {
            state.simulationStatus = 'running'
            const scenarioId = eventData.scenario_id as string
            if (!state.scenarioStates[scenarioId]) {
              state.scenarioStates[scenarioId] = {
                scenario: {
                  id: scenarioId,
                  name:
                    (eventData.scenario_name as string) ??
                    (eventData.persona as string) ??
                    'Unknown',
                  persona: {
                    personality: '',
                  },
                  goal: (eventData.goal as string) ?? '',
                  tags: [],
                  maxTurns: TURNS_LIMITS.DEFAULT,
                },
                status: 'running',
                turns: [],
              }
              state.scenarioOrder.push(scenarioId)
            } else {
              state.scenarioStates[scenarioId].status = 'running'
            }
            state.scenarioStates[scenarioId].flowRunUuid = eventData.flow_run_uuid as string
            break
          }

          case 'turn_completed': {
            const scenarioId = eventData.scenario_id as string
            if (state.scenarioStates[scenarioId]) {
              state.scenarioStates[scenarioId].turns.push({
                turn: eventData.turn as number,
                user: eventData.user as string,
                agent: eventData.agent as string,
                intent: (eventData.intent as string) ?? '',
                nodeUuid: (eventData.node_uuid as string) ?? '',
                variables: (eventData.variables as Record<string, unknown>) ?? {},
                variablesByNode:
                  (eventData.variables_by_node as Record<string, Record<string, unknown>>) ?? {},
                toolCalls: Array.isArray(eventData.tool_calls)
                  ? (eventData.tool_calls as RToolCall[])
                  : [],
                responseItems: Array.isArray(eventData.response_items)
                  ? (eventData.response_items as RResponseItem[])
                  : [],
                isInterruption: (eventData.is_interruption as boolean) ?? false,
                partialAssistantMsg: (eventData.partial_assistant_msg as string) ?? '',
              })
            }
            break
          }

          case 'scenario_completed': {
            const scenarioId = eventData.scenario_id as string
            if (state.scenarioStates[scenarioId]) {
              const s = state.scenarioStates[scenarioId]
              s.status = eventData.error ? 'error' : 'completed'
              s.result = {
                stopReason: (eventData.stop_reason as string) ?? 'unknown',
                turns: (eventData.turns as number) ?? s.turns.length,
                evaluation: eventData.evaluation as Record<string, unknown> | undefined,
                evalError: (eventData.eval_error as boolean) ?? false,
                error: eventData.error as string | undefined,
              }
            }
            break
          }

          case 'simulation_completed':
            state.simulationStatus = 'completed'
            break

          case 'simulation_cancelled':
            state.simulationStatus = 'cancelled'
            for (const id of state.scenarioOrder) {
              const s = state.scenarioStates[id]
              if (s && (s.status === 'running' || s.status === 'pending')) {
                s.status = 'cancelled'
              }
            }
            break

          case 'simulation_error':
            state.simulationStatus = 'error'
            state.errorMessage = (eventData.message as string) ?? 'Unknown error'
            break

          case 'stream_ended':
            if (isGenerationActive(state.generationStatus)) {
              state.generationStatus = 'completed'
              state.generationProgress = null
            }
            if (
              state.simulationStatus === 'running' ||
              state.simulationStatus === 'started' ||
              state.simulationStatus === 'enqueued'
            ) {
              state.simulationStatus = 'completed'
            }
            break
        }
      }),

    clear: () => set({ events: [], ...INITIAL_STRUCTURED_STATE }),
    selectScenario: (id) => set({ selectedScenarioId: id }),
    setSseController: (ctrl) => set({ sseController: ctrl }),
    cancelSimulation: () =>
      set((state) => {
        state.sseController?.abort()
        state.simulationStatus = 'cancelled'
        for (const id of state.scenarioOrder) {
          const s = state.scenarioStates[id]
          if (s && (s.status === 'running' || s.status === 'pending')) {
            s.status = 'cancelled'
          }
        }
      }),
  }),
)
